import { type CSSProperties, type ReactNode, useEffect, useRef, useState } from 'react';
import { useDialogFocusTrap } from './lib/focus-trap.ts';
import { useRegisterOverlay } from './map-layer.tsx';
import { Field, Label, Select } from './ui.tsx';

/**
 * The shared **rail-directory** shell — the one layout every rail screen
 * (People, Teams, Locations, Hardware, Activity) reads as, generalised out of
 * Settings' self-contained two-pane glass panel so the screens are one shape
 * rather than many.
 *
 * The model (Trav, 2026-09-07; cohort filter moved into the band 2026-09-15, and
 * from a chip row to a dropdown 2026-09-16) is an **inbox**: one full-width
 * toolbar band across the top — title, search, the cohort + screen filters, and
 * actions — then the full-width list beneath it, with a right slide-in inspect
 * overlay:
 *
 *   ┌──────────────────────────────────────────────┐
 *   │ title·count  search  [Group▾] [Site▾]  actions│  ← header band
 *   ├──────────────────────────────────────────────┤
 *   │  the list (full width)                        │
 *   └──────────────────────────────────────────────┘
 *
 * with the selected row's detail sliding in **from the right, over the list**
 * (never a permanent third pane) via {@link InspectOverlay}.
 *
 * - **The header band** spans the full content width and stays put
 *   (`sticky top-0`) while the list scrolls. It carries the screen title with
 *   its count, an optional labelled search, the cohort dropdown, any screen
 *   filter controls, and the add/upload action(s).
 * - **The cohort filter** is the second level: the categories/types within the
 *   entity, rendered as a labelled `<select>` in the band's filter lane, sitting
 *   beside the screen's own filters (the Site dropdown). Its live counts ride in
 *   the option text. It is **optional**: a directory with no second axis (Teams —
 *   a flat list of teams) passes no `types`, and the band shows just
 *   title/search/actions. (Until 2026-09-15 this was a 200px left column; it
 *   moved into the band, first as a chip row and then as this dropdown, so the
 *   list gets the full width and the filters read as one bar — Trav's calls.)
 * - **The list region** fills the full width beneath the band, so a building of
 *   hundreds scrolls beneath the fixed band rather than pushing it off screen.
 *
 * Settings is the reference for the [nav | content] shape and deliberately does
 * NOT consume this (it has no search and no overlay); the other four do.
 *
 * The whole thing renders as an ordinary glass panel over the persistent map
 * substrate — it is a child on the map, not a replacement for it.
 */

export type DirectoryType = {
  id: string;
  label: string;
  /** An optional live count, shown in the cohort dropdown's option text. */
  count?: number;
};

export function Directory({
  title,
  meta,
  typeColumnLabel,
  types,
  activeType,
  onSelectType,
  search,
  filters,
  actions,
  toolbar,
  children,
}: {
  title: ReactNode;
  /**
   * A quiet line of count beside the title — "33 active", plus "N of M shown"
   * when a filter narrows the list. It lives in the sticky header rather than as
   * a card/section title above the list, so the list itself renders edge-to-edge
   * (D, owner correction 2026-09-07).
   */
  meta?: ReactNode;
  /**
   * The visible/accessible label for the cohort `<select>`. Required only when
   * `types` is supplied — a directory with no second axis (Teams) omits both.
   */
  typeColumnLabel?: string;
  /**
   * The cohort entries. Absent or empty renders no cohort dropdown at all — just
   * the sticky header and the edge-to-edge list, for a directory (Teams) that
   * has no second axis to slice by.
   */
  types?: DirectoryType[];
  activeType?: string;
  onSelectType?: (id: string) => void;
  /** An optional labelled search box in the sticky header. */
  search?: {
    value: string;
    onChange: (v: string) => void;
    label?: string;
    placeholder?: string;
  };
  /** Filter controls (real `<select>`/`<input>` nodes) in the sticky header. */
  filters?: ReactNode;
  /** The add/upload/connect action(s) on the right of the sticky header. */
  actions?: ReactNode;
  /**
   * A full-width toolbar inside the sticky header, below the title row — used for
   * the bulk-action bar that rides above the list and stays reachable while the
   * list scrolls beneath the header. Rendered only when supplied.
   */
  toolbar?: ReactNode;
  /** The list region — the caller renders the rows and owns their scroll bound. */
  children: ReactNode;
}) {
  // The cohort dropdown appears only when there is a real choice to make: a
  // directory with no second axis (Teams) passes no `types`, and one with a
  // single cohort (Locations with no sensitive buildings, the People
  // error/empty state) would render a one-option select — pointless, so it is
  // omitted and the screen shows just its own filters.
  const hasCohort = Boolean(types && types.length > 1);

  // Measure the header band's rendered height and publish it as a CSS custom
  // property on the *root*, so a `stickyHeader` <Table> in the list pane (People,
  // Locations) can pin its column labels *directly beneath* the band rather than
  // behind it — the band's height changes with width and with the bulk-action
  // toolbar, so a hardcoded offset would be wrong.
  //
  // The offset must equal the band's **full visual footprint** down to the edge
  // the column header pins against. So the band owns all its spacing *inside* its
  // measured box — top/bottom padding and a bottom hairline, never a trailing
  // `margin` (a margin is outside `offsetHeight`, and the earlier `mb-5` was
  // exactly why the column header rode up over the first row: the published
  // offset fell short of where the band actually ended). Resting breathing room
  // below the band lives on the two-pane row's `mt`, which scrolls away and never
  // enters the offset. Guarded for jsdom, which has no ResizeObserver; the
  // one-shot measure on mount is enough there (offsetHeight is 0, a no-op).
  const headerRef = useRef<HTMLDivElement>(null);
  const [headerH, setHeaderH] = useState(0);
  useEffect(() => {
    const el = headerRef.current;
    if (!el) return;
    const update = () => setHeaderH(el.offsetHeight);
    update();
    if (typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  // If the active cohort is no longer one of the options — a team was deleted,
  // or the last restricted building was removed so the group collapses to just
  // "All" and the dropdown is hidden — snap the selection back to the first
  // cohort. Without this the list keeps filtering to a vanished cohort with no
  // visible control to recover the view (the "state with no way out" trap).
  useEffect(() => {
    if (types && types.length > 0 && activeType && !types.some((t) => t.id === activeType)) {
      onSelectType?.(types[0]!.id);
    }
  }, [types, activeType, onSelectType]);

  return (
    <div className="flex flex-col" style={{ ['--dir-header-h']: `${headerH}px` } as CSSProperties}>
      {/*
        The header band — the inbox's one top toolbar, spanning the full content
        width across both panes. Title on the left with its count, search and
        filters between, the add/upload actions on the right. It pins to the top
        of the scrolling region (`sticky top-0`), with a bottom hairline
        separating it from the panes. It is ordinary flow content, so it never
        traps keyboard focus, and it wraps sensibly at narrow widths. All its
        spacing is padding + border (inside `offsetHeight`) so the column header
        pins flush beneath it — see the measurement note above.

        The fill is the OPAQUE, theme-aware `--glass-solid` (`#ffffff` light /
        `#111a2e` dark), with no second `backdrop-filter`. Owner call
        2026-09-15, having weighed the only real trade: the list body is glass
        over the map, so an opaque band reads as a faint box against it — but the
        translucent `--sheet` alternative let the rows scrolling under this pinned
        strip ghost through it (verified in-browser). Box over ghost, and the
        token adapts to both themes. The one `.sheet` ancestor already owns the
        glass, so this strip adds no nested blur.
      */}
      <div
        ref={headerRef}
        // Full-bleed bar: the sheet (shell.tsx) wraps this in `--sheet-pad`,
        // which would otherwise inset the band's opaque fill and leave it
        // floating as a narrower white strip inside the sheet (Trav, 2026-09-16 —
        // "the nav bar doesn't stretch full width"). Negative margins of the
        // SAME `--sheet-pad` pull the fill out to the sheet's edges (sides and
        // top, so it reads as one bar flush under the banner), and matching `px`
        // puts the band's own content back in line with the list below. Both
        // read the one var, so they cannot drift; `py-3` is the band's own
        // vertical rhythm and is inside `offsetHeight` (the `--dir-header-h`
        // measurement note above depends on that).
        className="sticky top-0 z-20 -mx-[var(--sheet-pad)] -mt-[var(--sheet-pad)] border-b border-[var(--glass-edge)] bg-[var(--glass-solid)] px-[var(--sheet-pad)] py-3"
      >
        {/*
          One aligned toolbar row (E, owner correction 2026-09-07): the title,
          the search + filters, and the actions all sit on a single baseline
          (`items-end`), so the label-topped controls line up with the bottom of
          the title rather than floating above it. The title takes only its own
          width; search/filters flow next to it; the actions pin right
          (`ml-auto`). No staggering — everything reads as one bar.
        */}
        <div className="flex flex-wrap items-end gap-x-4 gap-y-3">
          <div className="flex min-w-0 shrink-0 flex-col">
            <h1 className="text-[25px] leading-[31px] font-[650] tracking-[-0.02em]">{title}</h1>
            {meta ? <span className="mt-0.5 text-[13px] text-[var(--ink-2)]">{meta}</span> : null}
          </div>
          {search ? (
            <div className="w-full max-w-[260px]">
              <Field
                label={search.label ?? 'Search'}
                type="search"
                value={search.value}
                onChange={search.onChange}
                placeholder={search.placeholder}
              />
            </div>
          ) : null}
          {/* The cohort filter and the screen's own filters share one lane, so
              the cohort dropdown sits directly beside the Site dropdown (Trav,
              2026-09-16 — it was a chip row until now). The cohort is a real
              `<select>` like the others: choosing a slice is still navigation
              between cohorts, but as a labelled dropdown it reads as one filter
              bar rather than a wrapping wall of chips that ate a viewport row on
              a big org. Cohort first (the primary axis), then the screen's
              filters. Omitted for a directory with no second axis (Teams). */}
          {hasCohort || filters ? (
            <div className="flex flex-wrap items-end gap-3">
              {hasCohort ? (
                <div className="w-[13.5rem]">
                  <Select
                    label={typeColumnLabel ?? 'Show'}
                    // A valid option id even for the frame before the reset
                    // effect above fixes a stale/absent selection, so the
                    // controlled <select> never sits on a value with no option.
                    value={
                      activeType && types!.some((t) => t.id === activeType)
                        ? activeType
                        : (types![0]?.id ?? '')
                    }
                    onChange={(v) => onSelectType?.(v)}
                    options={types!.map((t) => ({
                      value: t.id,
                      // The live count rides in the option text (a native
                      // <option> has nowhere else to carry it); omitted where a
                      // type has none, so a countless cohort reads cleanly.
                      label: typeof t.count === 'number' ? `${t.label} (${t.count})` : t.label,
                    }))}
                  />
                </div>
              ) : null}
              {filters}
            </div>
          ) : null}
          {actions ? (
            // Reserve the fixed theme toggle's footprint (h-9 w-9 at `right-3`,
            // shell.tsx) on the right so the primary CTA never sits under it at
            // narrow content widths — the toggle is `fixed` to the viewport corner
            // and the band's `ml-auto` actions lane otherwise runs straight into it.
            <div className="ml-auto flex shrink-0 items-end gap-2 pr-12">{actions}</div>
          ) : null}
        </div>

        {/* The bulk-action bar (or any full-width toolbar) rides in the band, so
            it stays put above the panes while the list scrolls. */}
        {toolbar ? <div className="mt-3">{toolbar}</div> : null}
      </div>

      {/* The list, full-width beneath the band, FLUSH against it (Trav
          2026-09-19 — "the column headers bar should not have a gap from the
          filter box"). No `mt`: the sticky column-header row pins directly under
          the band via `--dir-header-h`, and at rest it sits immediately beneath
          the band's hairline. The screen owns whether its list runs edge-to-edge
          (a single-table directory wraps its table in {@link FullBleedList}; the
          note/legend stay inset). */}
      <div>{children}</div>
    </div>
  );
}

/**
 * Pulls a rail-directory's list table out to the sheet edges, so its column
 * -header strip and row separators run edge-to-edge under the full-width
 * {@link Directory} band rather than sitting inset from it (Trav 2026-09-19,
 * People #470 — "the column headers bar should run to the edge").
 *
 * Negative `--sheet-pad` margins bleed the table to the sheet's edges; matching
 * first/last cell padding (the SAME var) puts the CONTENT back in line with the
 * band, so the two cannot drift. This is the People pattern named once so every
 * single-table directory list reads the same way — People, Teams and Locations
 * all wrap their `stickyHeader` table in it. A copied className in three files
 * would silently diverge the moment `--sheet-pad` changed, which is exactly the
 * one-source-of-truth reason `--sheet-pad` is a token in the first place.
 *
 * Scoped to the table: a note above or a legend below the list stays inset by
 * not being wrapped. Only for a `stickyHeader` {@link Table} inside a
 * `Directory` (whose band is itself full-bleed); a bounded/boxed table is not
 * bled.
 */
export function FullBleedList({ children }: { children: ReactNode }) {
  return (
    <div className="-mx-[var(--sheet-pad)] [&_td:first-child]:pl-[var(--sheet-pad)] [&_td:last-child]:pr-[var(--sheet-pad)] [&_th:first-child]:pl-[var(--sheet-pad)] [&_th:last-child]:pr-[var(--sheet-pad)]">
      {children}
    </div>
  );
}

/**
 * The right inspect overlay — the selected row's detail, sliding in **from the
 * right, over the list**. Not a permanent right pane: the list stays underneath
 * and re-reads its query the moment the overlay closes.
 *
 * It is a proper modal dialog (WCAG 2.2 AA): `role="dialog"` + `aria-modal`,
 * focus moves in on open and is trapped inside while it is open, Escape / the ✕
 * / a click on the backdrop all close it, and focus is restored to the invoking
 * row on close — all of that through the shared {@link useDialogFocusTrap} hook
 * (the same one the People and Teams drawers already used). On a narrow screen
 * it is full-width; on a wide one it is a right-hand panel, and `width="wide"`
 * gives a building-detail-sized surface room to breathe.
 */
export function InspectOverlay({
  ariaLabel,
  title,
  subtitle,
  onClose,
  width = 'default',
  children,
}: {
  /** The dialog's accessible name — e.g. "Manage Carlton Primary". */
  ariaLabel: string;
  title: ReactNode;
  subtitle?: ReactNode;
  onClose: () => void;
  /** `wide` for a large detail surface (a whole building); `default` otherwise. */
  width?: 'default' | 'wide';
  children: ReactNode;
}) {
  // Trap Tab/Shift-Tab within the panel, focus the close button on open, close
  // on Escape, and restore focus to the invoking control on close.
  const panelRef = useDialogFocusTrap<HTMLDivElement>(onClose);
  // Register against the ONE "overlay open" source of truth for the whole life
  // of this overlay, so the persistent map stops panning and drops out of the
  // tab order behind it (A). Null-safe when rendered outside a <MapSubstrate>.
  useRegisterOverlay();
  const w = width === 'wide' ? 'w-[min(960px,100%)]' : 'w-[min(460px,100%)]';

  return (
    // `pointer-events-auto` makes the overlay a live island even over Home,
    // whose <main> and its ancestors are `pointer-events-none` so the bare map
    // pans (A). The full-screen frost backdrop below is what then captures input
    // and keeps the map from panning while the overlay is open.
    <div className="pointer-events-auto fixed inset-0 z-50 flex justify-end">
      {/* The click-away backdrop is a **glass frost**, not a grey box: it blurs
          the list/map underneath so the panel reads as depth over the surface it
          came from, and you still see the list softly through it. The only fill
          is a very faint token wash (`--paper` at 20%, so it flips with the
          theme — a light frost in light, a dark frost in dark), never a flat
          grey/black scrim. `saturate` keeps the colour coming through rather than
          greying to smoke. Where `backdrop-filter` is unsupported the wash is all
          that remains, and at 20% that degrades to barely-tinted rather than to
          an opaque panel. It sits outside the panel ref, so the focus trap never
          lands on it. */}
      <button
        type="button"
        aria-label="Close"
        onClick={onClose}
        className="absolute inset-0 bg-[var(--paper)]/20 [backdrop-filter:blur(6px)_saturate(150%)] [-webkit-backdrop-filter:blur(6px)_saturate(150%)]"
      />
      {/* The panel slides in from the right once (reduced motion collapses it to
          instant via the global rule in styles.css), then holds still. */}
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-label={ariaLabel}
        className={`sheet elev-3 notif-in relative z-10 flex h-full ${w} flex-col overflow-y-auto border-l border-[var(--glass-edge)] p-5`}
      >
        <div className="mb-5 flex items-start justify-between gap-3">
          <div className="min-w-0">
            <h2 className="text-[22px] leading-7 font-[650]">{title}</h2>
            {subtitle ? <p className="mt-0.5 text-[13px] text-[var(--ink-2)]">{subtitle}</p> : null}
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-full border border-[var(--line-2)] bg-[var(--paper)] text-[var(--ink-2)] hover:text-[var(--ink)]"
          >
            <span aria-hidden>&times;</span>
          </button>
        </div>
        {children}
      </div>
    </div>
  );
}

/** A labelled block for the inspect overlay's information grid (dt/dd). */
export function InspectField({ label, children }: { label: string; children: ReactNode }) {
  return (
    <>
      <dt className="text-[13px] text-[var(--ink-2)]">{label}</dt>
      <dd className="m-0 text-[14px] text-[var(--ink)]">{children}</dd>
    </>
  );
}

/** A muted em-dash-style value that reads as "nothing here", never a real value. */
export function InspectMuted({ children }: { children: ReactNode }) {
  return <span className="text-[var(--ink-3)]">{children}</span>;
}

/** Re-exported so callers can build a section label inside an overlay without
 *  reaching past this module. */
export { Label as InspectLabel };
